import {AfterViewInit, Component, ElementRef, Inject, Input, Renderer2, ViewChild} from '@angular/core';
import {TextModel} from "@white-label/components/page-builder/models/text.model";
import {MainStoreService} from "@cybfed/core/store/main.store.service";
import {ModalService} from "@cybfed/ui-lib/modal/modal.service";
import {InlineEditModel} from "@white-label/components/page-builder/models/ai/inline-edit.model";
import {MediaStore} from "@cybfed/ui-lib/forms/image-input/media-gallery/media.store";
import {customSetTimeout} from "@cybfed/core/helpers/overrided-timers";
import {DOCUMENT} from "@angular/common";

@Component({
    selector: 'app-ai-component',
    standalone: true,
    templateUrl: './ai.component.html',
    styles: [`
    .edit-link-icon {
        position: absolute;
        top: 0;
        right: 0;
        background: #fff;
        border: 1px solid #ccc;
        border-radius: 50%;
        width: 20px;
        height: 20px;
        font-size: 14px;
        line-height: 20px;
        text-align: center;
        cursor: pointer;
        transform: translate(50%, -50%);
    }
    .href-edit-container {
        position: relative;
    }
    .href-edit-toolbar {
        display: flex; 
        gap: 5px; 
        margin-top: 5px;
    }
    .href-edit-input {
        width: 100%;
        box-sizing: border-box;
    }
    `]
})
export class AiComponent implements AfterViewInit {
    @ViewChild('contentContainer', { static: false }) contentContainer!: ElementRef;
    private currentlyHighlighted: HTMLElement | null = null;
    private isEditing = false;
    private editableItem: HTMLElement | null = null;
    @Input() componentModel: TextModel = new TextModel();
    private highlightableSelector = 'span, p, a, img, video, label, li, div';

    constructor(public mainStore: MainStoreService,
                private renderer: Renderer2,
                private mediaStore: MediaStore,
                @Inject(DOCUMENT) private _document: Document,
                private modalService: ModalService){
    }

    ngAfterViewInit() {
        this.wrapTextNodes(this.contentContainer.nativeElement);
        const leafElements = this.getLeafHighlightableElements();
        this.setupHoverHighlight(leafElements);
        this.addEditIconsForLinks(); // Add icons as soon as the page is rendered
    }

    /**
     * Wrap all text nodes in <span>.
     */
    private wrapTextNodes(element: HTMLElement) {
        const walker = this._document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
            acceptNode: (node) => {
                return node.nodeValue && node.nodeValue.trim() !== ''
                    ? NodeFilter.FILTER_ACCEPT
                    : NodeFilter.FILTER_REJECT;
            }
        });

        const textNodes: Text[] = [];
        let currentNode = walker.nextNode();
        while (currentNode) {
            textNodes.push(currentNode as Text);
            currentNode = walker.nextNode();
        }

        textNodes.forEach(textNode => {
            const span = this.renderer.createElement('span');
            this.renderer.appendChild(span, this.renderer.createText(textNode.nodeValue || ''));
            textNode.parentNode?.replaceChild(span, textNode);
        });
    }

    /**
     * Returns an array of "leaf" highlightable elements.
     */
    private getLeafHighlightableElements(): HTMLElement[] {
        const container = this.contentContainer.nativeElement;
        const candidates = Array.from(container.querySelectorAll(this.highlightableSelector)) as HTMLElement[];

        return candidates.filter(el => {
            // Exclude the container itself
            if (el === container) return false;

            // For IMG and VIDEO just return them
            if (el.tagName === 'IMG' || el.tagName === 'VIDEO') {
                return true;
            }

            // Check if it has highlightable descendants
            const descendants = el.querySelectorAll(this.highlightableSelector);
            if (descendants.length > 0 && Array.from(descendants).some(d => d !== el)) return false;

            // Otherwise, for textual elements, ensure it has text
            const textContent = el.textContent?.trim() || '';
            return textContent.length > 0;
        });
    }

    /**
     * Sets up event delegation for hover highlight and editing.
     */
    private setupHoverHighlight(leafElements: HTMLElement[]) {
        const container = this.contentContainer.nativeElement;

        this.renderer.listen(container, 'mouseover', (event: MouseEvent) => {
            if (this.isEditing) return; // If editing, do not highlight
            const target = event.target as HTMLElement;
            const highlightableParent = leafElements.find(el => el.contains(target));

            if (highlightableParent && highlightableParent !== this.currentlyHighlighted) {
                if (this.currentlyHighlighted) {
                    this.removeHighlight(this.currentlyHighlighted);
                }
                this.addHighlight(highlightableParent);
                this.currentlyHighlighted = highlightableParent;
            }
        });

        this.renderer.listen(container, 'mouseout', (event: MouseEvent) => {
            if (this.isEditing) return;
            const relatedTarget = event.relatedTarget as HTMLElement;
            if (this.currentlyHighlighted && (!relatedTarget || !this.currentlyHighlighted.contains(relatedTarget))) {
                this.removeHighlight(this.currentlyHighlighted);
                this.currentlyHighlighted = null;
            }
        });

        this.renderer.listen(container, 'click', (event: MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            if (this.isEditing) return;

            if(this.currentlyHighlighted && this.currentlyHighlighted.tagName !== 'A') {
                // For text/images/video inline edit
                this.startInlineEditing(this.currentlyHighlighted);
            }
        });
    }

    private addHighlight(el: HTMLElement) {
        this.renderer.setStyle(el, 'outline', '2px solid #00f');
        const currentPosition = window.getComputedStyle(el).position;
        if (currentPosition === 'static') {
            this.renderer.setStyle(el, 'position', 'relative');
            el.style.cursor = 'pointer';
        }
    }

    private removeHighlight(el: HTMLElement) {
        this.renderer.removeStyle(el, 'outline');
    }

    private startInlineEditing(element: HTMLElement) {
        let modelForChange: InlineEditModel;
        this.editableItem = element;
        this.isEditing = true;
        if(element.tagName === 'IMG') {
            this.mediaStore.showMediaLib = true;
            this.mediaStore.setImageResource = this.setImage.bind(this);
        } else if (element.tagName === 'VIDEO') {
            this.mediaStore.showMediaLib = true;
            this.mediaStore.setImageResource = this.setVideo.bind(this);
        } else {
            this.inlineEditText();
        }
    }

    private inlineEditText(){
        const originalText = this.editableItem!.textContent?.trim() || '';
        this.editableItem!.innerHTML = ''; // Clear current content

        const editContainer = this.renderer.createElement('div');
        this.renderer.setStyle(editContainer, 'position', 'relative');
        this.renderer.setStyle(editContainer, 'padding', '5px');
        this.renderer.setStyle(editContainer, 'background', 'transparent');

        const textarea = this.renderer.createElement('textarea');
        this.renderer.setStyle(textarea, 'width', '100%');
        this.renderer.setStyle(textarea, 'box-sizing', 'border-box');
        this.renderer.setStyle(textarea, 'background', 'transparent');
        this.renderer.setStyle(textarea, 'color', 'var(--normal)');
        this.renderer.setProperty(textarea, 'value', originalText);

        // Auto resize textarea
        const adjustHeight = () => {
            (textarea as HTMLTextAreaElement).style.height = 'auto';
            (textarea as HTMLTextAreaElement).style.height = (textarea as HTMLTextAreaElement).scrollHeight + 'px';
        };
        this.renderer.listen(textarea, 'input', adjustHeight);
        setTimeout(adjustHeight);

        const toolbar = this.renderer.createElement('div');
        this.renderer.setStyle(toolbar, 'position', 'absolute');
        this.renderer.setStyle(toolbar, 'top', '5px');
        this.renderer.setStyle(toolbar, 'right', '5px');
        this.renderer.setStyle(toolbar, 'display', 'flex');
        this.renderer.setStyle(toolbar, 'gap', '5px');

        // Save icon
        const saveIcon = this.renderer.createElement('span');
        this.renderer.setStyle(saveIcon, 'cursor', 'pointer');
        this.renderer.setStyle(saveIcon, 'background', 'green');
        this.renderer.setStyle(saveIcon, 'color', 'gray');
        this.renderer.setStyle(saveIcon, 'border', '1px solid green');
        this.renderer.setStyle(saveIcon, 'border-radius', '3px');
        this.renderer.setStyle(saveIcon, 'padding', '2px 5px');
        this.renderer.setProperty(saveIcon, 'title', 'Save changes');
        this.renderer.appendChild(saveIcon, this.renderer.createText('✔'));

        // Cancel icon
        const cancelIcon = this.renderer.createElement('span');
        this.renderer.setStyle(cancelIcon, 'cursor', 'pointer');
        this.renderer.setStyle(cancelIcon, 'background', 'red');
        this.renderer.setStyle(cancelIcon, 'border', '1px solid red');
        this.renderer.setStyle(cancelIcon, 'border-radius', '3px');
        this.renderer.setStyle(cancelIcon, 'color', 'gray');
        this.renderer.setStyle(cancelIcon, 'padding', '2px 5px');
        this.renderer.setProperty(cancelIcon, 'title', 'Cancel changes');
        this.renderer.appendChild(cancelIcon, this.renderer.createText('✖'));

        this.renderer.listen(saveIcon, 'click', () => {
            customSetTimeout(()=>{
                this.saveInlineEdit((textarea as HTMLTextAreaElement).value);
            })
        });

        this.renderer.listen(cancelIcon, 'click', () => {
            customSetTimeout(()=>{
                this.cancelInlineEdit(originalText);
            })
        });

        this.renderer.appendChild(toolbar, saveIcon);
        this.renderer.appendChild(toolbar, cancelIcon);
        this.renderer.appendChild(editContainer, textarea);
        this.renderer.appendChild(editContainer, toolbar);
        this.renderer.appendChild(this.editableItem!, editContainer);

        (textarea as HTMLTextAreaElement).focus();
    }

    private saveInlineEdit(newValue: string) {
        this.isEditing = false; // Done editing
        this.editableItem!.innerHTML = '';
        const textNode = this.renderer.createText(newValue);
        this.renderer.appendChild(this.editableItem!, textNode);
        this.reinitialize();
    }

    private cancelInlineEdit(originalText: string) {
        this.isEditing = false; // Done editing
        this.editableItem!.innerHTML = '';
        const textNode = this.renderer.createText(originalText);
        this.renderer.appendChild(this.editableItem!, textNode);
        this.reinitialize();
    }

    setImage(value: any) {
        this.editableItem!.setAttribute('src', value[0].s3Url);
        this.isEditing = false; // Done editing
        this.reinitialize();
    }

    setVideo(value: any) {
        this.editableItem!.children[0].setAttribute('src', value[0].s3Url);
        this.editableItem!.removeAttribute('poster');
        this.isEditing = false; // Done editing
        this.reinitialize();
    }

    // -------- New Methods for Link Editing --------

    private addEditIconsForLinks() {
        const container = this.contentContainer.nativeElement;
        const anchors = Array.from(container.querySelectorAll('a[href]')) as HTMLAnchorElement[];

        anchors.forEach(anchor => {
            // Ensure position: relative for the anchor to place the icon
            const anchorPos = window.getComputedStyle(anchor).position;
            if (anchorPos === 'static') {
                this.renderer.setStyle(anchor, 'position', 'relative');
            }

            const icon = this.renderer.createElement('span');
            this.renderer.addClass(icon, 'edit-link-icon');
            this.renderer.setStyle(icon, 'cursor', 'pointer');
            this.renderer.appendChild(icon, this.renderer.createText('🔗'));

            this.renderer.listen(icon, 'click', (event) => {
                event.stopPropagation();
                event.preventDefault();
                this.startHrefEdit(anchor);
            });

            // Append the icon to the anchor
            this.renderer.appendChild(anchor, icon);
        });
    }

    private startHrefEdit(anchor: HTMLAnchorElement) {
        if (this.isEditing) return; // Avoid nested editing
        this.isEditing = true;
        this.editableItem = anchor;

        // Store original href
        const originalHref = anchor.getAttribute('href') || '';
        const originalContent = anchor.innerHTML;

        anchor.innerHTML = '';
        const editContainer = this.renderer.createElement('div');
        this.renderer.addClass(editContainer, 'href-edit-container');

        const input = this.renderer.createElement('input');
        this.renderer.addClass(input, 'href-edit-input');
        this.renderer.setProperty(input, 'value', originalHref);

        const toolbar = this.renderer.createElement('div');
        this.renderer.addClass(toolbar, 'href-edit-toolbar');

        const saveBtn = this.renderer.createElement('button');
        this.renderer.appendChild(saveBtn, this.renderer.createText('Save'));

        const cancelBtn = this.renderer.createElement('button');
        this.renderer.appendChild(cancelBtn, this.renderer.createText('Cancel'));

        this.renderer.listen(saveBtn, 'click', (event) => {
            const newHref = (input as HTMLInputElement).value.trim();
            event.stopPropagation();
            event.preventDefault();

            if (newHref) {
                anchor.setAttribute('href', newHref);
            }
            this.isEditing = false;
            // Restore the original text (or re-wrap if needed)
            anchor.innerHTML = originalContent;
            this.reinitialize();
        });

        this.renderer.listen(cancelBtn, 'click', (event) => {
            event.stopPropagation();
            event.preventDefault();
            // Revert
            this.isEditing = false;
            anchor.innerHTML = originalContent;
            this.reinitialize();
        });

        this.renderer.appendChild(toolbar, saveBtn);
        this.renderer.appendChild(toolbar, cancelBtn);
        this.renderer.appendChild(editContainer, input);
        this.renderer.appendChild(editContainer, toolbar);

        this.renderer.appendChild(anchor, editContainer);

        (input as HTMLInputElement).focus();
    }

    private reinitialize() {
        const elementsToRemove = this.contentContainer?.nativeElement?.querySelectorAll('.edit-link-icon');
        elementsToRemove.forEach(element => element.remove());
        this.componentModel.content.value = this.contentContainer.nativeElement.innerHTML;
        customSetTimeout(()=>{
            // Re-wrap text nodes and re-add icons after editing
            this.wrapTextNodes(this.contentContainer.nativeElement);
            const leafElements = this.getLeafHighlightableElements();
            this.setupHoverHighlight(leafElements);
            this.addEditIconsForLinks();
        });
    }

}
